import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../common/ApiClient';
import { InsertDraftsResponseType } from './schema/EditSessionSchema';
import { useAsyncBarrier } from './useAsyncBarrier';
import { RcFile } from 'antd/es/upload';

export type DraftableRow = {
  id: number;
};

export interface EditSessionMethods {
  updateRowsFn: (params: {
    sessionId: string;
    rows: { rowId: number; payload: unknown }[];
  }) => Promise<unknown>;
  insertRowsFn: (params: {
    sessionId: string;
    payloads: unknown[];
  }) => Promise<InsertDraftsResponseType>;
  insertExcelRowsFn?: (params: {
    sessionId: string;
    file: string | Blob | RcFile;
  }) => Promise<unknown>;
  deleteRowsFn: (params: {
    sessionId: string;
    rowIds: number[];
  }) => Promise<void>;
  commitFn: (params: {
    sessionId: string;
    confirmWarnings?: boolean;
  }) => Promise<void>;
  discardFn: (params: { sessionId: string }) => Promise<void>;
  validateRowFn: (params: {
    sessionId: string;
    payload: unknown;
  }) => Promise<ApiError[]>;
}

export interface UseEditSessionProps<
  Row extends DraftableRow,
> extends EditSessionMethods {
  sessionId: string | undefined;
  refreshRows: () => unknown;
  onSessionClose: (reopen: boolean) => unknown;
  getRowKey?: (row: Row) => number;
  reloadAfterInsert?: boolean;
}

export interface UseEditSessionReturn<Row extends DraftableRow = DraftableRow> {
  sessionId: string | undefined;
  updateRow: (payload: Row) => void;
  insertRows: (payloads: unknown[]) => Promise<number[]>;
  insertRow: (payload: unknown) => Promise<number>;
  insertExcelRows: (file: string | Blob | RcFile) => Promise<void>;
  deleteRow: (payload: Row) => void;
  deleteRows: (payloads: Row[]) => void;
  validateRow: (payload: unknown) => Promise<ApiError[]>;
  commitEdits: (confirmWarnings?: boolean) => Promise<CommitResult>;
  flushEdits: () => Promise<void>;
  registerCommitPreparation: (preparation: CommitPreparation) => () => void;
  discardEdits: (handleSessionClose?: () => unknown) => Promise<void>;
  resetSession: () => Promise<void>;
  refreshRows: () => unknown;
  isPending: boolean;
}

export type CommitPreparationResult = 'ready' | 'blocked';
export type CommitResult = 'committed' | 'blocked';
export type CommitPreparation = () => Promise<CommitPreparationResult>;

const DEBOUNCE_MS = 1000;

export const useEditSession = <Row extends DraftableRow>({
  sessionId,
  refreshRows: refreshRowsProp,
  onSessionClose,
  updateRowsFn,
  insertRowsFn,
  insertExcelRowsFn,
  deleteRowsFn,
  commitFn,
  discardFn,
  validateRowFn,
  getRowKey = defaultGetRowKey,
  reloadAfterInsert = true,
}: UseEditSessionProps<Row>): UseEditSessionReturn<Row> => {
  const [hasAnyUnsentDrafts, setHasAnyUnsentDrafts] = useState(false);
  const pendingDraftsRef = useRef<Map<number, any>>(new Map());
  const commitPreparationRef = useRef<CommitPreparation | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const excelImportRefreshTimersRef = useRef<Set<number>>(new Set());
  const sessionCloseRef = useRef<{
    sessionId: string;
    request?: Promise<void>;
  } | null>(null);

  const refreshRowsRef = useRef(refreshRowsProp);
  useEffect(() => {
    refreshRowsRef.current = refreshRowsProp;
  }, [refreshRowsProp]);
  const onSessionCloseRef = useRef(onSessionClose);
  useEffect(() => {
    onSessionCloseRef.current = onSessionClose;
  }, [onSessionClose]);
  const refreshRows = useCallback(() => refreshRowsRef.current(), []);

  // A hook to reliably manage many asynchronous edit session related operations and track their completion.
  const {
    run: runOperation,
    wait: waitForOperations,
    isPending: isOperationPending,
  } = useAsyncBarrier();

  // Register a commit preparation function that can be called before committing edits.
  const registerCommitPreparation = useCallback(
    (preparation: CommitPreparation) => {
      commitPreparationRef.current = preparation;
      return () => {
        if (commitPreparationRef.current === preparation) {
          commitPreparationRef.current = null;
        }
      };
    },
    [],
  );

  const clearPending = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingDraftsRef.current.clear();
    setHasAnyUnsentDrafts(false);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      excelImportRefreshTimersRef.current.forEach(clearTimeout);
      excelImportRefreshTimersRef.current.clear();
    };
  }, []);

  const flushPending = useCallback(() => {
    if (sessionId == null || pendingDraftsRef.current.size === 0) {
      // No pending drafts to flush, return a resolved promise.
      return Promise.resolve();
    }

    const rows = Array.from(pendingDraftsRef.current, ([rowId, payload]) => ({
      rowId,
      payload,
    }));

    clearPending();
    return runOperation(() => updateRowsFn({ sessionId, rows })).catch(
      (error) => {
        // If the update fails, restore the pending drafts and rethrow the error.
        rows.forEach(({ rowId, payload }) => {
          if (!pendingDraftsRef.current.has(rowId)) {
            pendingDraftsRef.current.set(rowId, payload);
          }
        });
        setHasAnyUnsentDrafts(true);
        throw error;
      },
    );
  }, [clearPending, runOperation, sessionId, updateRowsFn]);

  const updateRow = useCallback(
    (payload: Row) => {
      if (sessionId == null) {
        throw new Error('Edit session not open');
      }

      const rowId = getRowKey(payload);

      pendingDraftsRef.current.set(rowId, payload);
      setHasAnyUnsentDrafts(true);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(
        () => void flushPending().catch(() => undefined),
        DEBOUNCE_MS,
      );
    },
    [flushPending, getRowKey, sessionId],
  );

  const insertRows = useCallback(
    async (payloads: unknown[]) => {
      if (sessionId == null) {
        throw new Error('Edit session not open');
      }

      if (payloads.length === 0) {
        return [];
      }

      return runOperation(async () => {
        const { insertedIds } = await insertRowsFn({ sessionId, payloads });
        if (insertedIds.length !== payloads.length) {
          throw new Error('Inserted rows were not returned with ids');
        }
        if (reloadAfterInsert) {
          await refreshRows();
        }
        return insertedIds;
      });
    },
    [sessionId, insertRowsFn, refreshRows, runOperation, reloadAfterInsert],
  );

  const insertRow = useCallback(
    async (payload: unknown) => (await insertRows([payload]))[0],
    [insertRows],
  );

  const insertExcelRows = useCallback(
    async (file: string | Blob | RcFile) => {
      if (sessionId == null) {
        throw new Error('Edit session not open');
      }

      if (!insertExcelRowsFn) {
        return;
      }

      return runOperation(async () => {
        await insertExcelRowsFn({
          sessionId,
          file,
        });
        await refreshRows();
      });
    },
    [sessionId, insertExcelRowsFn, runOperation, refreshRows],
  );

  const deleteRows = useCallback(
    (payloads: Row[]) => {
      if (sessionId == null) {
        throw new Error('Edit session not open');
      }

      const rowIds: number[] = [];
      for (const payload of payloads) {
        const rowId = getRowKey(payload);
        rowIds.push(rowId);
        pendingDraftsRef.current.delete(rowId);
      }

      if (pendingDraftsRef.current.size === 0) {
        setHasAnyUnsentDrafts(false);
      }

      void runOperation(() =>
        deleteRowsFn({ sessionId, rowIds }).then(() => {
          void refreshRows();
        }),
      );
    },
    [sessionId, getRowKey, deleteRowsFn, refreshRows, runOperation],
  );

  const deleteRow = useCallback(
    (payload: Row) => deleteRows([payload]),
    [deleteRows],
  );

  const validateRow = useCallback(
    (payload: unknown) => {
      if (sessionId == null) {
        return Promise.resolve<ApiError[]>([]);
      }

      return runOperation(() => validateRowFn({ sessionId, payload }));
    },
    [sessionId, validateRowFn, runOperation],
  );

  // Send debounced drafts right away and wait for all running operations to complete
  const flushEdits = useCallback(async () => {
    await waitForOperations();
    await flushPending();
    await waitForOperations();
  }, [flushPending, waitForOperations]);

  // Commit edits by first running any registered commit preparation, then flushing pending drafts, and finally committing the edits
  const commitEdits = useCallback(
    async (confirmWarnings?: boolean) => {
      if (sessionId == null) {
        throw new Error('Edit session not open');
      }

      const preparationResult = await commitPreparationRef.current?.();
      if (preparationResult === 'blocked') {
        return 'blocked';
      }

      await flushEdits();
      await runOperation(() => commitFn({ sessionId, confirmWarnings }));
      sessionCloseRef.current = { sessionId };
      void onSessionCloseRef.current(true);
      return 'committed';
    },
    [commitFn, flushEdits, runOperation, sessionId],
  );

  const discardEdits = useCallback(
    (handleSessionClose?: () => unknown) => {
      if (sessionId == null) {
        // No session is currently open
        return Promise.resolve();
      }

      if (sessionCloseRef.current?.sessionId === sessionId) {
        // Session is already closed or being closed
        return sessionCloseRef.current.request ?? Promise.resolve();
      }

      clearPending();
      const request = runOperation(() => discardFn({ sessionId }))
        .then(() => {
          sessionCloseRef.current = { sessionId };
          handleSessionClose
            ? handleSessionClose()
            : onSessionCloseRef.current(false);
        })
        .catch((error) => {
          if (sessionCloseRef.current?.request === request) {
            // If the request that failed is the one currently tracked, clear the session close reference
            sessionCloseRef.current = null;
          }
          throw error;
        });
      sessionCloseRef.current = { sessionId, request };
      return request;
    },
    [sessionId, discardFn, clearPending, runOperation],
  );

  // Discard the session and stay in edit mode, so that a new session picks up data changed outside of the drafts
  const resetSession = useCallback(
    () => discardEdits(() => onSessionCloseRef.current(true)),
    [discardEdits],
  );

  return {
    sessionId,
    updateRow,
    insertRows,
    insertRow,
    insertExcelRows,
    deleteRow,
    deleteRows,
    validateRow,
    commitEdits,
    flushEdits,
    registerCommitPreparation,
    discardEdits,
    resetSession,
    refreshRows,
    isPending: isOperationPending || hasAnyUnsentDrafts,
  };
};

const defaultGetRowKey = (row: DraftableRow) => row.id;
